import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from 'reactstrap';
import type { PickListItem } from '../../api/pickLists';

export interface ConfirmDialogProps {
  isOpen: boolean;
  pickListExternalId: string;
  items: PickListItem[];
  isPending: boolean;
  /** Colli già registrati sulla lista; `null` = non ancora contati. */
  colli: number | null;
  /** Imballo già scelto sulla lista; `null` = non ancora scelto. */
  imballo: string | null;
  /**
   * I due valori arrivano solo se il dialog li ha CHIESTI (cioè se mancavano):
   * quando sono già presenti la conferma resta l'azione di prima.
   */
  onSubmit: (colliImballo?: { colli: number; imballo: string }) => void;
  onCancel: () => void;
}

/**
 * evasioni-correzioni-parziale — conferma pick-list = SOLA modal di conferma.
 * Rimosse note/motivazioni + il secondo stage (tabella righe inevase + checkbox):
 * la conferma di una lista parziale è un'azione esplicita (click Conferma), senza
 * campi obbligatori. Il `force_partial_accept` lo invia il parent in base ai parziali.
 *
 * Colli e imballo: si chiedono QUI **solo se mancano**. Per le liste parziali la
 * conferma è l'unico momento in cui una persona è già davanti alla lista, quindi
 * chiederli altrove significherebbe un passaggio in più. Se sono già stati
 * registrati dal dettaglio, il dialog resta quello di prima.
 *
 * Sono obbligatori perché senza la lista non verrebbe inviata a Book: confermarla
 * e lasciarla ferma sarebbe il peggiore dei due mondi — l'operatore crede di aver
 * chiuso, e la consegna non parte.
 */
export function ConfirmDialog({
  isOpen, pickListExternalId, items, isPending, colli, imballo, onSubmit, onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation('picklist');
  const [colliVal, setColliVal] = useState('');
  const [imballoVal, setImballoVal] = useState('');
  const [error, setError] = useState<string | null>(null);
  if (!isOpen) return null;

  // `== null` intenzionale: copre sia `null` sia `undefined`. Con il confronto
  // stretto, un chiamante che dimenticasse la prop passerebbe `undefined` e il
  // dialog li tratterebbe come GIA' INSERITI, saltando la richiesta in silenzio
  // — e la lista resterebbe poi ferma senza partire verso Book.
  const chiede = colli == null || imballo == null;

  const submit = () => {
    if (!chiede) { onSubmit(); return; }
    setError(null);
    const c = Number(colliVal);
    // La scelta vuota cade qui insieme ai colli non validi.
    if (colliVal.trim() === '' || imballoVal === ''
        || !Number.isInteger(c) || c < 0) {
      setError(t('colliImballo.invalid'));
      return;
    }
    onSubmit({ colli: c, imballo: imballoVal });
  };

  const totalReq = items.reduce((s, it) => s + it.qty_required, 0);
  const totalPicked = items.reduce((s, it) => s + it.qty_picked, 0);

  return (
    <div role="dialog" aria-modal="true" aria-label={t('confirm_dialog.aria_label', { label: pickListExternalId })}
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
         onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: 'var(--vp-bg)', padding: 16, borderRadius: 'var(--vp-r-md)',
                    minWidth: 420, maxWidth: 560, boxShadow: 'var(--vp-shadow-lg)' }}>
        <h5 className="fs-15 mb-2">{t('confirm_dialog.stage_initial_title', { label: pickListExternalId })}</h5>
        <p className="small text-muted mb-3">
          {t('confirm_dialog.stage_initial_summary', { rows: items.length, picked: totalPicked, required: totalReq })}
        </p>
        {chiede && (
          <div className="d-flex flex-column gap-2 mb-3">
            <Label className="mb-0 small fw-semibold">{t('colliImballo.label')}</Label>
            <div className="d-flex align-items-center gap-2">
              <Input type="number" min={0} step={1} style={{ width: 120 }}
                     aria-label={t('colliImballo.colli')} placeholder={t('colliImballo.colli')}
                     value={colliVal} onChange={(e) => setColliVal(e.target.value)} />
              <Input type="select" style={{ width: 160 }}
                     aria-label={t('colliImballo.imballo')}
                     value={imballoVal} onChange={(e) => setImballoVal(e.target.value)}>
                <option value="">{t('colliImballo.choose')}</option>
                <option value="Scatole">{t('colliImballo.scatole')}</option>
                <option value="Bancali">{t('colliImballo.bancali')}</option>
              </Input>
            </div>
            <span className="form-text text-muted mb-0">{t('colliImballo.blocksExport')}</span>
            {error && <span role="alert" className="text-danger small">{error}</span>}
          </div>
        )}
        {/* La conferma e' il punto di non ritorno per le quantita', e va detto
            PRIMA del clic. Dal 30/09 il Prel. si corregge solo a lista Evasa:
            appena questa lista diventa Confermata il campo sparisce, e se resta
            ferma in attesa di colli e imballo non si corregge piu' — «Riapri»
            non e' esposto in dashboard.

            Niente `role="alert"`: quel canale in questa finestra e' dell'errore
            di validazione dell'imballo. Usarlo qui farebbe annunciare un
            problema che non c'e', e renderebbe il vero errore indistinguibile. */}
        <p className="small text-muted d-flex align-items-start gap-2 mb-0 mt-3">
          <i className="bi bi-exclamation-triangle text-warning mt-1" aria-hidden="true" />
          <span>{t('confirm_dialog.picked_frozen_warning')}</span>
        </p>
        <div className="d-flex gap-2 justify-content-end mt-3">
          <Button type="button" color="light" onClick={onCancel} disabled={isPending}>{t('confirm_dialog.cancel')}</Button>
          <Button type="button" color="primary" disabled={isPending} onClick={submit}>
            {isPending ? t('confirm_dialog.submitting_short') : t('confirm_dialog.submit_short')}
          </Button>
        </div>
      </div>
    </div>
  );
}
